<template>
  <AuthLayout :mode="mode">
    <Section heading="Edit Contact Settings" :mode="mode" />
    <div class="grid gap-6 sm:gap-4">
      <div class="grid grid-cols-2 gap-6 sm:grid-cols-1 sm:gap-4">
        <AppButton title="Update information" @action="updateContact" />
        <AppButton
          :isLink="true"
          :to="{ name: 'contact-set-password', params: { id: id } }"
          title="Update password"
          type="secondary"
        />
      </div>
      <AppCard
        :mode="mode"
        title="Name Settings"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="first-name"
              title="First name"
              placeholder="Not specified"
              v-model="contactData.first_name"
            />
            <AppInput
              :mode="mode"
              id="last-name"
              title="Last name"
              placeholder="Not specified"
              v-model="contactData.last_name"
            />
            <AppInput
              :mode="mode"
              id="title"
              title="Title"
              placeholder="Not necessarily"
              v-model="contactData.title"
            />
            <AppInput
              :mode="mode"
              id="username"
              title="Username"
              placeholder="Not specified"
              v-model="contactData.username"
            />
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Phone Numbers"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="work-phone"
              type="tel"
              title="Work phone"
              placeholder="Not specified"
              v-model="contactData.work_phone"
            />
            <AppInput
              :mode="mode"
              id="home-phone"
              type="tel"
              title="Home phone"
              placeholder="Not specified"
              v-model="contactData.home_phone"
            />
            <AppInput
              :mode="mode"
              id="mobile-phone"
              type="tel"
              title="Mobile phone"
              placeholder="Not specified"
              v-model="contactData.mobile_phone"
            />
            <AppInput
              :mode="mode"
              id="fax"
              type="tel"
              title="Fax"
              placeholder="Not specified"
              v-model="contactData.fax"
            />
            <AppInput
              :mode="mode"
              id="other-phone"
              type="tel"
              title="Other phone"
              placeholder="Not specified"
              v-model="contactData.other_phone"
            />
            <AppInput
              :mode="mode"
              id="toll"
              title="Toll free"
              placeholder="Not specified"
              v-model="memberData.toll_free_phone"
              :disabled="type === 'add'"
            />
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Address Information"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="address-1"
              title="Address 1"
              placeholder="Not specified"
              v-model="contactData.address1"
            />
            <AppInput
              :mode="mode"
              id="address-2"
              title="Address 2"
              placeholder="Not specified"
              v-model="contactData.address2"
            />
            <AppSelect
              :mode="mode"
              title="Country"
              v-model="contactData.country"
            >
              <template #options>
                <option value="">Country</option>
                <option
                  v-for="(country, key) in countries"
                  :value="country.code"
                  :key="key"
                  :selected="country.code === contactData.country"
                >
                  {{ country.country }}
                </option>
              </template>
            </AppSelect>
            <AppInput
              :mode="mode"
              id="zip"
              title="Zip"
              placeholder="Not specified"
              v-model="contactData.zip"
            />
            <AppSelect
              :mode="mode"
              title="City"
              v-model="contactData.city"
              :isDisabled="cities.length === 0"
            >
              <template #options>
                <option value="">City</option>
                <option
                  v-for="(city, key) in cities"
                  :value="city.name"
                  :key="key"
                  :selected="city.name === contactData.city"
                >
                  {{ city.name }}
                </option>
              </template>
            </AppSelect>
            <AppSelect
              :mode="mode"
              title="State"
              v-model="contactData.state"
              :isDisabled="states.length === 0"
            >
              <template #options>
                <option value="">State</option>
                <option
                  v-for="(state, key) in states"
                  :value="state.code"
                  :key="key"
                  :selected="state.code === contactData.state"
                >
                  {{ state.name }}
                </option>
              </template>
            </AppSelect>
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Other Communication Channels"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="email"
              type="email"
              title="Email"
              placeholder="Not specified"
              v-model="contactData.email"
            />
            <AppInput
              :mode="mode"
              id="linkedin"
              title="Linkedin"
              placeholder="Not specified"
              v-model="contactData.linkedin_url"
            />
            <div class="col-span-2 sm:col-span-1">
              <AppCheckbox
                :mode="mode"
                id="out-office"
                v-model="contactData.on_vacation"
                title="I am out of the office, do not email"
              />
            </div>
          </div>
        </template>
      </AppCard>
      <AppCard
        v-if="canEditAdministrator"
        :mode="mode"
        title="Membership Role"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppCheckbox
              :mode="mode"
              id="administrator"
              v-model="contactData.administrator"
              title="Member Administrator"
            />
          </div>
        </template>
      </AppCard>
      <AppCard
        v-if="isAdmin"
        :mode="mode"
        title="Manufacturers we Deal with"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="relative flex items-center">
            <Search
              :size="20"
              :stroke-width="1.75"
              class="absolute left-4 opacity-40"
            />
            <AppInput
              :mode="mode"
              type="search"
              placeholder="Search by manufacturer"
              v-model="mfr_company_query"
              classes="!pl-11"
              :hasLabel="false"
            />
          </div>
          <div class="mt-4">
            <template
              v-if="filteredManufactuers && filteredManufactuers.length > 0"
            >
              <AppTable :mode="mode" :columns="columns" :isHidden="false">
                <template #rows>
                  <tr class="select-all-row">
                    <td>
                      <AppCheckbox
                        :mode="mode"
                        id="select-all-is-rep"
                        v-model="allIsRep"
                        title="Select all"
                        classes="checkbox-long"
                      />
                    </td>
                    <td>
                      <div class="flex justify-center">
                        <AppCheckbox
                          :mode="mode"
                          id="select-all-buy"
                          v-model="allInstantBuy"
                        />
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center">
                        <AppCheckbox
                          :mode="mode"
                          id="select-all-sell"
                          v-model="allInstantSell"
                        />
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center">
                        <AppCheckbox
                          :mode="mode"
                          id="select-all-summary"
                          v-model="allRecSummary"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr v-for="(mfr, key) in filteredManufactuers" :key="key">
                    <td>
                      <AppCheckbox
                        :mode="mode"
                        :id="mfr.mfr_id"
                        v-model="mfr.is_rep"
                        :title="mfr.company ? mfr.company : '-'"
                        classes="checkbox-long"
                      />
                    </td>
                    <td>
                      <div class="flex justify-center">
                        <AppCheckbox
                          :mode="mode"
                          :id="mfr.mfr_id + '-' + 'buy'"
                          v-model="mfr.instant_buy"
                        />
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center">
                        <AppCheckbox
                          :mode="mode"
                          :id="mfr.mfr_id + '-' + 'sell'"
                          v-model="mfr.instant_sell"
                        />
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center">
                        <AppCheckbox
                          :mode="mode"
                          :id="mfr.mfr_id + '-' + 'summary-email'"
                          v-model="mfr.rec_summary_email"
                        />
                      </div>
                    </td>
                  </tr>
                </template>
              </AppTable>
            </template>
            <EmptyData
              v-else
              :notFound="'No manufacturers found!'"
              :mode="mode"
            />
          </div>
        </template>
      </AppCard>
      <div class="grid grid-cols-2 gap-6 sm:grid-cols-1 sm:gap-4">
        <AppButton title="Update information" @action="updateContact" />
        <AppButton
          :isLink="true"
          :to="{ name: 'contact-set-password', params: { id: id } }"
          title="Update password"
          type="secondary"
        />
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import { Search } from "lucide-vue-next";
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import AuthLayout from "@A/AuthLayout.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppTable from "@C/Tables/AppTable.vue";
import EmptyData from "@C/Common/EmptyData.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "UpdateContact",
  components: {
    AuthLayout,
    Section,
    Search,
    AppCard,
    AppCheckbox,
    AppTable,
    EmptyData,
    AppInput,
    AppSelect,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      id: "",
      contactData: {},
      memberData: {},
      states: [],
      cities: [],
      debounceTimer: null,
      mfr_company_query: "",
      columns: [
        {
          name: "Manufacturers",
        },
        {
          name: "Buy",
          classes: "w-[50px]",
        },
        {
          name: "Sell",
          classes: "w-[50px]",
        },
        {
          name: "Summary Email",
          classes: "w-[150px]",
        },
      ],
    };
  },
  created() {
    this.id = this.$route.params.id;
  },
  mounted() {
    this.getContact();
  },
  methods: {
    async getContact() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/contact/${this.id}`);
        if (data && data.contact) {
          this.contactData = data.contact;
          this.memberData = data.member;
          if (!data.contact.hasOwnProperty("administrator")) {
            this.contactData.administrator = false;
          }
          if (!data.contact.hasOwnProperty("chair")) {
            this.contactData.chair = null;
          }
          if (!data.contact.hasOwnProperty("board_end_date")) {
            this.contactData.board_end_date = "";
          }
          if (!data.contact.hasOwnProperty("board_start_date")) {
            this.contactData.board_start_date = "";
          }
          if (!data.contact.hasOwnProperty("board_type")) {
            this.contactData.board_type = null;
          }
          if (!data.contact.hasOwnProperty("press")) {
            this.contactData.press = false;
          }
          if (!data.contact.hasOwnProperty("billing_contact")) {
            this.contactData.billing_contact = false;
          }
          if (!data.ok_to_update && this.isAdmin) {
            return;
          }
          if (!data.ok_to_update) {
            this.toastStore.show(
              "You do not have permission to update this contact",
              "error",
            );
            this.$router.push({ name: "settings" });
          }
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async updateContact() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        contact: this.contactData,
        member: this.memberData,
      };
      try {
        const { data } = await ApiRequest().post(`/contact`, payload);
        if (data && data.contact) {
          this.toastStore.show(`Contact updated successfully!`, "success");
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async getLocations() {
      if (!this.contactData.country || !this.contactData.zip) {
        this.states = [];
        this.cities = [];
        return;
      }
      try {
        const payload = {
          country_code: this.$getCountryCode(
            this.countries,
            this.contactData.country,
          ),
          postal_code: this.contactData.zip,
        };
        const response = await fetch("/public_api/postal_codes", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          this.toastStore.show("Failed to fetch location data", "error");
          return;
        }
        const data = await response.json();
        if (!data) {
          this.toastStore.show("No location data found", "error");
          this.states = [];
          this.cities = [];
          return;
        }
        const statesMap = new Map();
        const citiesMap = new Map();
        data.forEach((item) => {
          const source = item._source;
          if (source.admin_code1) {
            statesMap.set(source.admin_code1, {
              code: source.admin_code1,
              name: source.admin_name1 || source.admin_code1,
            });
          }
          if (source.place_name) {
            citiesMap.set(source.place_name, {
              name: source.place_name,
            });
          }
        });
        this.states = Array.from(statesMap.values());
        this.cities = Array.from(citiesMap.values());
      } catch (e) {
        this.handleError(e);
        this.states = [];
        this.cities = [];
      }
    },
    debouncedGetLocations() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.getLocations();
      }, 500);
    },
  },
  watch: {
    "contactData.country": function (newVal) {
      if (newVal && this.contactData.zip) {
        this.debouncedGetLocations();
      }
    },
    "contactData.zip": function (newVal) {
      if (newVal && this.contactData.country) {
        this.debouncedGetLocations();
      }
    },
  },
  computed: {
    filteredManufactuers() {
      const mfrs = this.contactData?.mfrs;
      if (!mfrs?.length) {
        return [];
      }
      return mfrs.filter((mfr) =>
        mfr.company
          .toLowerCase()
          .includes(this.mfr_company_query.toLowerCase()),
      );
    },
    allIsRep: {
      get() {
        const list = this.filteredManufactuers;
        return list.length > 0 && list.every((m) => m.is_rep);
      },
      set(v) {
        this.filteredManufactuers.forEach((m) => (m.is_rep = v));
      },
    },
    allInstantBuy: {
      get() {
        const list = this.filteredManufactuers;
        return list.length > 0 && list.every((m) => m.instant_buy);
      },
      set(v) {
        this.filteredManufactuers.forEach((m) => (m.instant_buy = v));
      },
    },
    allInstantSell: {
      get() {
        const list = this.filteredManufactuers;
        return list.length > 0 && list.every((m) => m.instant_sell);
      },
      set(v) {
        this.filteredManufactuers.forEach((m) => (m.instant_sell = v));
      },
    },
    allRecSummary: {
      get() {
        const list = this.filteredManufactuers;
        return list.length > 0 && list.every((m) => m.rec_summary_email);
      },
      set(v) {
        this.filteredManufactuers.forEach((m) => (m.rec_summary_email = v));
      },
    },
    countries() {
      return this.countriesStore.countries;
    },
    isAdmin() {
      return this.userStore.user.global_admin;
    },
    canEditAdministrator() {
      // Global admins always; member administrators when the contact being
      // edited belongs to their own member.
      if (this.isAdmin) return true;
      const me = this.userStore.user || {};
      const myMemberId = me.member && me.member.member_id;
      const targetMemberId = this.memberData && this.memberData.member_id;
      return (
        me.contact &&
        me.contact.administrator === true &&
        myMemberId &&
        targetMemberId &&
        myMemberId === targetMemberId
      );
    },
  },
};
</script>
